import { getRegionDisplayName } from './roiCalculator';

export function generateBlueprintData({
  scenario = 'A',
  roles = [],
  summary = {},
  region = 'DE',
  setupFee = 25000,
  horizonMonths = 36,
  rampPercent = 25,
  implementationWeeks = 4,
  handoverUplift = 40,
  legacyRampDownModel = 'A',
  legacyRampDownDuration = 3,
  companyName: companyNameInput = 'Helvetia Logistics SaaS',
  focusAreas: focusAreasInput = ['R&D / Product', 'QA', 'DevOps & Support'],
  techStack: techStackInput = ['React', 'Spring Boot', 'PostgreSQL', 'AWS'],
  deliveryModel = 'BOT / Nano GCC',
  industry = 'Logistics software',
}) {
  const teamSize = roles.reduce((sum, role) => sum + Number(role.fte || 0), 0);
  const scenarioLabel = scenario === 'B' ? 'Scenario B' : scenario === 'C' ? 'Scenario C' : 'Scenario A';
  const companyName = companyNameInput || 'Helvetia Logistics SaaS';
  const focusAreas = focusAreasInput && focusAreasInput.length ? focusAreasInput : ['R&D / Product', 'QA', 'DevOps & Support'];
  const techStack = techStackInput && techStackInput.length ? techStackInput : ['React', 'Spring Boot', 'PostgreSQL', 'AWS'];

  const monthlyLocal = summary.localTotal ?? roles.reduce((sum, role) => sum + (Number(role.fte || 0) * (Number(role.localPerFte) || 0)), 0);
  const monthlySpringUp = summary.springUpTotal ?? roles.reduce((sum, role) => sum + (Number(role.fte || 0) * (Number(role.springUpPerFte) || 0)), 0);
  const totalLocalCost = summary.totalLocalCost ?? monthlyLocal * Number(horizonMonths);
  const totalSpringUpCost = summary.totalSpringUpCost ?? monthlySpringUp * Number(horizonMonths) + Number(setupFee);
  const netSavings = summary.netSavings ?? totalLocalCost - totalSpringUpCost;
  const savingsPct = summary.savingsPct ?? (monthlyLocal > 0 ? ((monthlyLocal - monthlySpringUp) / monthlyLocal) * 100 : 0);
  const roi = summary.roi ?? (totalSpringUpCost > 0 ? (netSavings / totalSpringUpCost) * 100 : 0);
  const paybackMonths = summary.paybackMonths ?? (monthlyLocal - monthlySpringUp > 0 ? Number(setupFee) / (monthlyLocal - monthlySpringUp) : 0);
  const totalDuration = summary.effectiveRamp ? `${Math.max(3, Math.round(summary.effectiveRamp * 10) / 10)} mo` : `${Math.max(4, implementationWeeks)} wk`;

  const roleLines = (roles.length ? roles : []).map((role) => {
    const seniority = role.seniority || 'Senior';
    const roleType = role.roleType || 'Backend';
    const fte = Number(role.fte || 0);
    const name = role.name || `${roleType} Engineer`;
    const title = `${name} — ${seniority} · ${roleType} · ${fte} FTE`;
    return { title, fte };
  });

  const overviewText = scenario === 'C'
    ? `SpringUp Labs will establish a dedicated transition team for ${companyName} under a replacement model, with legacy ramp-down and handover uplift reflected in the plan.`
    : scenario === 'B'
      ? `SpringUp Labs will extend ${companyName} with a focused add-on capability that accelerates delivery without a large greenfield ramp.`
      : `SpringUp Labs will establish a dedicated, self-managed team for ${companyName} under a Build-Operate-Transfer model that starts with a senior core and scales into a full operating department.`;

  const timeline = [
    { label: 'Search & selection', weeks: 5, color: 'var(--navy)' },
    { label: 'Due diligence & KT', weeks: 4, color: 'var(--teal)' },
    { label: 'Reverse KT', weeks: 2, color: '#8a6d1f' },
    { label: 'Pilot & shadowing', weeks: 3, color: 'var(--blue)' },
    { label: 'Ramp to full op.', weeks: 6, color: '#5a7cc2' },
    { label: 'Steady state', weeks: 0, color: 'var(--green)' },
  ];

  const totalWidth = timeline.reduce((sum, item) => sum + (item.weeks || 0), 0) || 1;
  const timelineBars = timeline.map((item) => ({
    ...item,
    width: item.weeks ? `${(item.weeks / totalWidth) * 100}%` : '23.08%',
  }));

  const governanceText = 'Biweekly status meetings, sprint-end delivery audits, performance reviews, and central knowledge management in Confluence/Jira.';

  const generatedText = [
    `${companyName} · ${scenarioLabel}`,
    '',
    '1 · Engagement overview',
    overviewText,
    '',
    '2 · Team composition',
    ...roleLines.map((role) => `- ${role.title}`),
    '',
    '3 · ROLE PROFILES',
    ...roleLines.map((role) => `- ${role.title}`),
    '',
    '4 · Implementation approach',
    'Plan → Build → Operate → Transfer. The department is built around a senior core and follows SpringUp delivery best practice.',
    '',
    '5 · Phased plan & quality gates',
    '- Search & selection — ~5 wk · gate: client confirms senior core',
    '- Due diligence & KT — ~4 wk · gate: KT completeness review',
    '- Reverse KT — ~2 wk · gate: reverse-presentation evaluation',
    '- Pilot & shadowing — ~3 wk · gate: onboarding sign-off',
    '- Ramp to full op. — ~6 wk · gate: steady-state readiness',
    '',
    '6 · Timeline',
    `Total to full operation ≈ ${totalDuration}`,
    '',
    '7 · Why SpringUp implements it this way',
    'Senior core first — Experienced leads set the operating model and protect quality.',
    'Knowledge transfer as a gate — The transfer is measured before the team operates independently.',
    'Cost-effective expansion — The delivery model stays capacity-matched while avoiding local hiring friction.',
    '',
    '8 · Governance & KPIs',
    'Biweekly status meetings', 
    'Biweekly status meetings', 
    'sprint-end delivery audits',
    'performance reviews',
    'central knowledge management in Confluence/Jira.',
    '',
    '9 · Investment, cost & ROI',
    `Run-cost comparison — Local ${getRegionDisplayName(region)} team at ${Math.round(monthlyLocal).toLocaleString('de-DE')} € /mo vs SpringUp at ${Math.round(monthlySpringUp).toLocaleString('de-DE')} € /mo.`,
    `Investment — One-time setup fee ${Math.round(setupFee).toLocaleString('de-DE')} € .`,
    `Return — Net savings ${Math.round(netSavings).toLocaleString('de-DE')} € over ${horizonMonths} months.`,
    `ROI — ${Math.round(roi)}% (net ÷ SpringUp spend).`,
    `Payback — Month ${Math.max(1, Math.round(paybackMonths))}.`,
    '',
    '7 · Governance & KPIs',
    // governanceText is exposed separately so the UI can render comma-separated items as bullets
    // keep the same sentence here for backward compatibility
    'Biweekly status meetings, sprint-end delivery audits, performance reviews, and central knowledge management in Confluence/Jira.',
    '',
    '8 · Risks & assumptions',
    '- Client availability for interviews and KT during early phases is required; delays extend the ramp and adjust ROI.',
    '- Local benchmarks are researched DE/CHF/US suggestions; replace with your real costs. SpringUp rates are cost-plus placeholders until quoted.',
  ].join('\n');

  return {
    companyName,
    scenarioLabel,
    focusAreas,
    techStack,
    teamSize,
    totalDuration,
    overviewText,
    roleLines,
    setupFee,
    summary: {
      ...summary,
      monthlyLocal,
      monthlySpringUp,
      totalLocalCost,
      totalSpringUpCost,
      netSavings,
      savingsPct,
      roi,
      paybackMonths,
    },
    governanceText,
    generatedText,
    timelineBars,
    implementationWeeks,
    handoverUplift,
    legacyRampDownModel,
    legacyRampDownDuration,
    region,
    rampPercent,
    horizonMonths,
  };
}
